"use client";

import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { useMonthlyUsageStats } from "@/hooks/use-monthly-usage-stats";
import { UsageEmpty } from "@/components/usage-empty";
import { calculateUsageChange, type UsageChange } from "@/lib/usage-comparison";
import { UsageStatCard } from "@/components/usage-stat-card";
import { UsageUnavailable } from "@/components/usage-unavailable";

interface StatItem extends UsageChange {
  title: string;
  value: string;
  description: string;
}

const formatNumber = (num: number | undefined): string =>
  (num ?? 0).toLocaleString();

export function YouzuLensStats() {
  const { currentStats, previousStats, error, previousError, isLoading, previousLoading } = useMonthlyUsageStats();

  if (error) return <UsageUnavailable />;

  if (isLoading) {
    return (
      <div className="grid gap-4 md:grid-cols-2">
        {[1, 2].map((i) => (
          <Card
            key={i}
            className="bg-gradient-to-br from-purple-50 to-pink-50 border border-purple-100"
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <div className="h-4 w-24 bg-purple-200 animate-pulse rounded" />
            </CardHeader>
            <CardContent>
              <div className="h-8 w-16 bg-purple-200 animate-pulse rounded mb-2" />
              <div className="h-3 w-32 bg-purple-200 animate-pulse rounded" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (!currentStats) return <UsageEmpty />;

  const prev = previousStats || {
    youzuLens: 0,
    youzuLensUsers: 0,
    objectDetection: 0,
    objectDetectionUsers: 0,
    propertyVisualize: 0,
    propertyVisualizeUsers: 0,
    roomVisualizer: 0,
    roomVisualizerUsers: 0,
    magicBackground: 0,
    magicBackgroundUsers: 0,
    twoDTo3D: 0,
    twoDTo3DUsers: 0,
    swap: 0,
    swapUsers: 0,
    period: "",
  };
  const stats: StatItem[] = [
    {
      title: "Total Searches",
      value: formatNumber(currentStats.youzuLens),
      description: "Visual searches this month",
      ...calculateUsageChange(currentStats.youzuLens, prev.youzuLens),
    },
    {
      title: "Unique Users",
      value: formatNumber(currentStats.youzuLensUsers),
      description: "Users who used Lens",
      ...calculateUsageChange(currentStats.youzuLensUsers, prev.youzuLensUsers),
    },
  ];

  return (
    <div className="grid gap-4 md:grid-cols-2">
      {stats.map((stat) => (
        <UsageStatCard key={stat.title} title={stat.title} value={stat.value}
          description={stat.description} comparison={previousError ? calculateUsageChange(undefined) : stat}
          period="previous month" comparisonLoading={previousLoading} />
      ))}
    </div>
  );
}
